import { Capacitor } from '@capacitor/core'
import { InAppBrowser } from '@capgo/capacitor-inappbrowser'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { useCallback } from 'react'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { globalDefinitions } from '@/defined-translations'
import { mixpanel } from '@/mixpanel'
import { isHylyghtActivityCompleted } from '@/modules/hylyght/helpers/hylyght'
import { useHylyght } from '@/modules/hylyght/hooks/useHylyght'
import { RelatedTriggers } from '@/modules/shared/components/related-items/RelatedTriggers'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { useToast } from '@/modules/shared/hooks/useToast'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { useTaskStatusMetaData } from '../../hooks/useTaskStatusMetaData'
import { StTaskDetailParagraph, TaskDetailLayout } from '../detail-layout/TaskDetailLayout'

import type { TaskOutletContext } from '../../pages/TaskRoot.page'
import type { HylyghtTask } from '../../types/task.types'

type Props = {
  task: HylyghtTask
  refetch: () => void
}

export const HylyghtDetail = ({ task, refetch }: Props) => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()

  const { showErrorToast } = useToast()
  const isLoggedInForMandatee = useLoggedInForMandatee()
  const { daysTillExpiry } = useTaskStatusMetaData(task)

  const { reloadAndSelectTask } = useOutletContext<TaskOutletContext>()

  const { getHylyghtActivityById } = useHylyght()

  // Template variables
  // ------------------------------------------------------------------------- /

  const isCompleted = task.status === 'completed'
  const isExpired = task.status === 'expired'

  const isHylyghtProgram: boolean = task.activity?.type === 'program'

  // Methods
  // ------------------------------------------------------------------------- /

  /**
   * Syncs the Hylyght activity for the current task and updates the task state
   * if the completedDates array has changed.
   *
   * This ensures the UI reflects the most up-to-date completion status for the activity.
   *
   * @returns {Promise<void>} Resolves when the sync is complete.
   */
  const syncHylyghtActivity = useCallback(async () => {
    if (!task.activity?.id || task.status === 'completed') {
      return
    }

    const hylyghtActivity = await getHylyghtActivityById(task.activity.id, task.activity.type)

    const newHylyghtActivityIsCompleted = isHylyghtActivityCompleted(hylyghtActivity)

    if (newHylyghtActivityIsCompleted) {
      refetch()
      reloadAndSelectTask(task.id)
    }
  }, [task, getHylyghtActivityById, reloadAndSelectTask, refetch])

  const handleCtaPress = async () => {
    const hylyghtMagicLink = task.activity?.link

    // Validate Hylyght URL
    if (!task.activity || !hylyghtMagicLink) {
      showErrorToast({
        message: t({
          id: 'hylyght-detail.warning-modal.error',
          message: 'Geen geldige Hylyght URL gevonden. Probeer het later opnieuw.',
        }),
      })
      return
    }

    // Track event
    mixpanel.track(`${location}_hylyght_open`, {
      url: hylyghtMagicLink,
      is_mandate: isLoggedInForMandatee,
    })

    // Handle native platform (mobile)
    if (Capacitor.isNativePlatform()) {
      await handleNativePlatform(hylyghtMagicLink)
      return
    }

    // Handle web platform
    handleWebPlatform(hylyghtMagicLink)
  }

  // Native platform specific handler
  const handleNativePlatform = async (hylyghtMagicLink: string) => {
    try {
      await InAppBrowser.clearCookies({ url: hylyghtMagicLink })
      await InAppBrowser.openWebView({
        url: hylyghtMagicLink,
        headers: {
          'user-agent': `Mozilla/5.0 (iPhone; CPU iPhone OS16_5_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/16.5.2 Mobile/15E148 Safari/604.1 mijnZiekenhuis`,
        },
        title: task.activity?.name ?? 'Hylyght',
        closeModal: false,
      })

      // Update activity when browser is closed. Store the handle and remove it
      // inside the callback so each open doesn't accumulate a new listener.
      const closeListener = await InAppBrowser.addListener('closeEvent', async () => {
        await closeListener.remove()

        if (!task.activity) return

        try {
          await syncHylyghtActivity()
        } catch (error) {
          console.error('Failed to refetch Hylyght activity:', error)
        }
      })
    } catch (error) {
      console.error('Failed to open Hylyght in native browser:', error)
    }
  }

  // Web platform specific handler
  const handleWebPlatform = (hylyghtMagicLink: string) => {
    let hylyghtDeeplink = `/hylyght?hylyghtUrl=${window.encodeURIComponent(
      hylyghtMagicLink,
    )}&activityId=${task.id}&hylyghtId=${task.activity?.id}&hylyghtType=${task.activity?.type}`

    if (task.activity) {
      hylyghtDeeplink += `&hylyghtType=${task.activity.type}`
      hylyghtDeeplink += `&hylyghtId=${task.activity.id}`
    }

    navigate(hylyghtDeeplink)
  }

  const getStatusDisplay = (): string => {
    if (isHylyghtProgram && (isCompleted || isExpired)) {
      return i18n._(globalDefinitions.task.Hylyght.expired_program, {
        date: formatLocale(task.activity?.enddate ?? task.updatedAt, 'dd/MM/yyyy'),
      })
    }

    if (isCompleted) {
      return i18n._(globalDefinitions.task.Hylyght.completed, {
        date: formatLocale(task.updatedAt, 'dd/MM/yyyy'),
      })
    }

    if (task.recurring) {
      return i18n._(globalDefinitions.task.recurring)
    }

    if (isExpired) {
      return i18n._(globalDefinitions.task.expired)
    }

    if (daysTillExpiry === 0) {
      return i18n._(globalDefinitions.task.Hylyght.expires_today)
    }

    if (daysTillExpiry === 1) {
      return i18n._(globalDefinitions.task.Hylyght.expires_in_one_day)
    }

    if (daysTillExpiry && daysTillExpiry > 1) {
      return i18n._(globalDefinitions.task.Hylyght.expires_in_x_days, {
        count: daysTillExpiry,
      })
    }

    return i18n._(globalDefinitions.task.Hylyght.other, {
      date: formatLocale(task.plannedAt, 'dd/MM/yyyy'),
    })
  }

  // Render
  // ------------------------------------------------------------------------- /

  /**
   * Render the date section
   * @returns The date section
   */
  const renderDateSection = () => {
    if (!task.activity || !task.plannedAt || !isHylyghtProgram) {
      return null
    }

    const startDate = formatLocale(task.activity.startdate, 'dd/MM/yyyy')
    const endDate = formatLocale(task.activity.enddate, 'dd/MM/yyyy')

    return (
      <FlexColumn gap={theme.UI.Spacing.Space2}>
        <StTaskDetailParagraph>Startdatum: {startDate}</StTaskDetailParagraph>
        <StTaskDetailParagraph>Einddatum: {endDate}</StTaskDetailParagraph>
      </FlexColumn>
    )
  }

  const renderContent = () => {
    if (isHylyghtProgram && (isCompleted || isExpired)) {
      return (
        <FlexColumn gap={theme.UI.Spacing.Space3}>
          <StTaskDetailParagraph>
            {i18n._(globalDefinitions.task.Hylyght.completed_program_description)}
          </StTaskDetailParagraph>
        </FlexColumn>
      )
    }

    if (task.status === 'pending') {
      return isHylyghtProgram ? (
        <FlexColumn gap={theme.UI.Spacing.Space3}>
          <H3>
            {t({
              id: 'hylyght-detail.program',
              message: 'Deze taak is terugkerend',
            })}
          </H3>
          <StTaskDetailParagraph style={{ whiteSpace: 'pre-line' }}>
            {t({
              id: 'hylyght-detail.program.description',
              message:
                'Een terugkerende taak wordt voor langere tijd in je agenda weergegeven. Je hoeft de taak niet dagelijks uit te voeren. Hou je aan het advies en voorschrift van je zorgprofessional.\n\nAangeboden door Hylyght',
            })}
          </StTaskDetailParagraph>
        </FlexColumn>
      ) : (
        <FlexColumn gap={theme.UI.Spacing.Space3}>
          <StTaskDetailParagraph style={{ whiteSpace: 'pre-line' }}>
            {t({
              id: 'hylyght-detail.questionnaire',
              message: 'Dit is een wekelijkse vragenlijst aangeboden door Hylyght.',
            })}
          </StTaskDetailParagraph>
        </FlexColumn>
      )
    }

    if (isExpired) {
      return (
        <FlexColumn>
          <StTaskDetailParagraph>
            {t({
              id: 'hylyght-detail.expired.title',
              message: 'Deze taak werd helaas niet tijdig afgewerkt en is intussen afgesloten.',
            })}
          </StTaskDetailParagraph>
        </FlexColumn>
      )
    }

    return null
  }

  return (
    <TaskDetailLayout>
      <TaskDetailLayout.Header task={task} statusLabel={getStatusDisplay()} />

      <TaskDetailLayout.Content>
        <FlexColumn gap={theme.UI.Spacing.Space4}>
          {renderDateSection()}
          {renderContent()}

          {/* Related activities */}
          {task?.trigger && <RelatedTriggers trigger={task.trigger} />}
        </FlexColumn>
      </TaskDetailLayout.Content>

      <TaskDetailLayout.Footer>
        <Button variant='Primary' onClick={handleCtaPress}>
          {t({
            id: 'hylyght-detail.cta.start',
            message: 'Bekijken',
          })}
        </Button>
      </TaskDetailLayout.Footer>
    </TaskDetailLayout>
  )
}

// Utility functions
// ------------------------------------------------------------------------- /

/**
 * Translate the interval to a human readable string
 * @param interval The interval to translate
 * @returns The translated interval
 */
export function transltateInterval(interval: string | undefined) {
  if (!interval) return ''

  const translations = {
    daily: t({
      id: 'hylyght-detail.interval.daily',
      message: 'dagelijkse',
    }),
    weekly: t({
      id: 'hylyght-detail.interval.weekly',
      message: 'wekelijkse',
    }),
    monthly: t({
      id: 'hylyght-detail.interval.monthly',
      message: 'maandelijkse',
    }),
  }

  return (
    translations[interval as keyof typeof translations] ??
    t({
      id: 'hylyght-detail.interval.default',
      message: 'terugkerende',
    })
  )
}
